/**
 * #620 (also N-21 / REQ-6133, #609) — pure helpers pulled out of
 * `RequirementsTreeView.js` so the "multi-project, null `activeProject`" case
 * can be regression-tested directly, without mounting the whole view (which
 * has no dedicated Jest harness — see the #620 evidence in the batch report).
 *
 * The bug this guards: the tree's open-comment dots (and, before #609, the
 * approval-activity probe) used to be keyed on `activeProject` — a user
 * SELECTION that is `null` whenever the tree shows several projects grouped,
 * which is the NORMAL view for any org with more than one project. Both were
 * silently keying their fetch on `null` and never loading anything. The fix is
 * to key on the caller's full set of VIEW-PERMITTED projects instead —
 * `computeWatchedProjectIds` below never reads `activeProject` at all, by
 * construction — and to fetch/merge PER PROJECT so one unreadable project
 * can't blank the dots for the rest — `mergeOpenCommentItemUuids` below.
 */

/**
 * The set of projects a "what's happening across everything I can see" probe
 * should watch: every project the caller is a MEMBER of (`myProjects` — NOT
 * `activeProject`, which is a selection and routinely null), narrowed to the
 * ones the user has chosen to see (`visibleProjects`, when they've picked a
 * subset) — otherwise every membership.
 * @param {Array<{id: string}>|null} myProjects
 * @param {string[]|null} visibleProjects
 * @returns {string[]}
 */
export function computeWatchedProjectIds(myProjects, visibleProjects) {
  const all = (myProjects || []).map((p) => p && p.id).filter(Boolean);
  if (Array.isArray(visibleProjects) && visibleProjects.length) {
    return all.filter((id) => visibleProjects.includes(id));
  }
  return all;
}

/**
 * Merge `GET /items/open-comment-items-batch`'s per-project response
 * (`{ items: { [projectId]: { item_uuids: [...] } } }`) into ONE flat Set of
 * item uuids — the shape `RequirementsTree.jsx`'s dot renderer reads. This is
 * the exact multi-project case #620 pins: a real response carries an entry
 * PER watched project, and every project's items must end up in the merged
 * set regardless of how many projects were watched or which (if any) is the
 * user's `activeProject` — this function never takes `activeProject` as an
 * input at all, so there is nothing here that COULD be keyed on it.
 * @param {{items?: Object<string, {item_uuids?: string[]}>}} batchResponse
 * @returns {Set<string>}
 */
export function mergeOpenCommentItemUuids(batchResponse) {
  const byProject = (batchResponse && batchResponse.items) || {};
  const next = new Set();
  Object.keys(byProject).forEach((pid) => {
    ((byProject[pid] && byProject[pid].item_uuids) || []).forEach((u) => next.add(u));
  });
  return next;
}
